import { HomeConfig } from "situview-webapi-client";

export class Utils {
  static getColorPool(): string[] {
    return [
      "rgba(255, 141, 74, 1)",
      "rgba(255, 89, 94, 1)",
      "rgba(255, 202, 58, 1)",
      "rgba(138, 201, 38, 1)",
      "rgba(20, 193, 245, 1)",
      "rgba(25, 130, 196, 1)",
      "rgba(106, 76, 147, 1)"
    ];
  }

  static shuffleArray(array: any[]): any[] {
    let m = array.length, t, i;

    while (m) {
      i = Math.floor(Math.random() * m--);
      t = array[m];
      array[m] = array[i];
      array[i] = t;
    }

    return array;
  }

  static changeRgbaOpacity(color: string, opacity: string): string {
    return color.replace(/[\d\.]+\)$/g, `${opacity})`);
  }

  static capitalizeFirstLetter(text: string): string {
    if (text) {
      return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
    }
    return "";
  }

  static getHomeConfigurationFromPath(path: string): HomeConfig | null {
    let s = path.replace("\\", "/").replace("//", "/");
    let x = s.split('/');

    if (x.length < 5) {
      return null;
    }

    try {
      let options = new Array<string>();
      if (x[4]?.toLowerCase() != "default") {
        options = x[4]?.split(',');
      }

      let homeConfig = new HomeConfig();
      homeConfig.builder = x[1];
      homeConfig.home = x[2];
      homeConfig.masterFile = x[3];
      homeConfig.options = options;
      // facades include a config at the end
      if (path.startsWith("facade")) {
        homeConfig.themes = x[5]?.split("\\")[0].split("~");
      } else {
        homeConfig.themes = x[5]?.split("~");
      }

      return homeConfig;
    } catch {
      return null;
    }
  }

  static getHomeConfigurationFromUrl(path: string): HomeConfig | null {
    let s = path.replace("\\", "/").replace("//", "/");
    let x = s.split('/');

    if (x.length < 5) {
      return null;
    }

    try {
      x.reverse();
      let options = new Array<string>();
      if (x[3]?.toLowerCase() != "default") {
        options = x[3]?.split(',');
      }

      let homeConfig = new HomeConfig();
      homeConfig.builder = x[6];
      homeConfig.home = x[5];
      homeConfig.masterFile = x[4];
      homeConfig.options = options;
      // facades include a config at the end
      if (path.includes("facade")) {
        homeConfig.themes = x[2]?.split("\\")[0].split("~");
      } else {
        homeConfig.themes = x[2]?.split("~");
      }

      return homeConfig;
    } catch {
      return null;
    }
  }

  static timeAgoString(date?: Date): string {
    if (date === undefined) {
      return 'Never';
    }
  
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) {
      return 'A few seconds ago';
    }
  
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} ${this.pluralize('minute', minutes)} ago`;
    }
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} ${this.pluralize('hour', hours)} ago`;
    }
    
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days} ${this.pluralize('day', days)} ago`;
    }
    
    const weeks = Math.floor(days / 7);
    if (weeks < 5) {
      return `${weeks} ${this.pluralize('week', weeks)} ago`;
    }
    
    const months = Math.floor(days / 30);
    if (days < 365) {
      return `${months} ${this.pluralize('month', months)} ago`;
    }
    
    const years = Math.floor(days / 365);
    return `${years} ${this.pluralize('year', years)} ago`;
  }
  
  static pluralize(unit: string, value: number): string {
    return value === 1 ? unit : `${unit}s`;
  }

  /**
   * Sorts an array in place.
   * This method mutates the array and returns a reference to the same array.
   * @param array The array to sort.
   * @param property The property to sort by, either the property name string or get the property using a function.
   * @param ascending Sort by ascending order (true: ascending, false: descending)
   * ```ts
   * dynamicSort(myArray, "propertyName");
   * ```
   * ```ts
   * dynamicSort(myArray, x => x.propertyName);
   * ```
   */
  static dynamicSort<T>(array: T[], property: keyof T | ((x: T) => any), ascending = true): T[] {
    return array.sort((a, b) => {
        let aValue, bValue;

        if (typeof property === 'function') {
          aValue = property(a);
          bValue = property(b);
        } else {
          aValue = a[property];
          bValue = b[property];
        }

        if (aValue === bValue) {
          return 0;
        }

        var aIsUnset = aValue === undefined || aValue === null;
        var bIsUnset = bValue === undefined || bValue === null;

        if (aIsUnset && !bIsUnset) {
          return ascending ? 1 : -1;
        }
        
        if (!aIsUnset && bIsUnset) {
          return ascending ? -1 : 1;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) {
          return ascending ? 1 : -1;
        } else {
          return ascending ? -1 : 1;
        }
    });
  }

  static exportToCsv(filename: string, rows: any[][]) {
    var processRow = function (row: any[]) {
      var finalVal = '';
      for (var j = 0; j < row.length; j++) {
        var innerValue = row[j] === null ? '' : row[j].toString();
        if (row[j] instanceof Date) {
          innerValue = row[j].toLocaleString();
        };
        var result = innerValue.replace(/"/g, '""');
        if (result.search(/("|,|\n)/g) >= 0)
          result = '"' + result + '"';
        if (j > 0)
          finalVal += ',';
        finalVal += result;
      }
      return finalVal + '\n';
    };

    var csvFile = '';
    for (var i = 0; i < rows.length; i++) {
      csvFile += processRow(rows[i]);
    }

    var blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
    if ((navigator as any).msSaveBlob) { // IE 10+
      (navigator as any).msSaveBlob(blob, filename);
    } else {
      var link = document.createElement("a");
      if (link.download !== undefined) { // feature detection
        // Browsers that support HTML5 download attribute
        var url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  static getFileExtension(fileName: string): string {
    return fileName.split('.').pop() || '';
  }

  static getCurrentTimestamp(): string {
    const now = new Date();
    return now.toISOString().replace(/[\-\:\.]/g, ''); // Format: 'YYYYMMDDTHHMMSS'
  }

  static isColorDark(color: string): boolean {
    let r = 0, g = 0, b = 0;
    
    if (color.startsWith('#')) {
      const hex = color.substring(1);
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    } else if (color.startsWith('rgb')) {
      const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10);
        g = parseInt(rgbMatch[2], 10);
        b = parseInt(rgbMatch[3], 10);
      }
    }
    
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128; 
  }

  /**
 * Converts a hex color string (#RGB or #RRGGBB) to an rgba color string
 * @param hex The hex color string to convert
 * @param alpha The opacity value (0-1), defaults to 0.8
 * @returns An rgba color string
 */
  static hexToRgba(hex: string, alpha: number = 0.8): string {
    // Default return value in case of invalid input
    const defaultRgba = 'rgba(128, 128, 128, 0.8)';
    
    if (!hex || typeof hex !== 'string') {
      return defaultRgba;
    }

    // Remove # if present
    hex = hex.replace(/^#/, '');
    
    // Parse hex values based on length
    let r, g, b;
    
    if (hex.length === 3) {
      // Shorthand format #RGB
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    } else if (hex.length === 6) {
      // Full format #RRGGBB
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      // Invalid format
      return defaultRgba;
    }
    
    // Check if parsing was successful
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return defaultRgba;
    }
    
    // Ensure alpha is between 0 and 1
    alpha = Math.max(0, Math.min(1, alpha));
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
